
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { SecurityEnhancedAuth } from './SecurityEnhancedAuth';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from "@/lib/utils";

const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password too long')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number'),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});

interface ResetPasswordFormProps {
  onSuccess?: () => void;
}

export function ResetPasswordForm({ onSuccess }: ResetPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  
  const form = useForm<{ password: string; confirmPassword: string }>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Enhanced session validation with security logging
  useEffect(() => {
    const validateSession = async () => {
      try {
        // Check for valid session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          toast({
            title: "Authentication Error",
            description: "Unable to verify your session. Please request a new password reset.",
            variant: "destructive",
          });
          setIsValidating(false);
          return;
        }

        if (!session?.user) {
          toast({
            title: "Invalid Reset Link",
            description: "This password reset link is invalid or has expired. Please request a new one.",
            variant: "destructive",
          });

          // Redirect to auth page after a delay
          setTimeout(() => {
            navigate('/auth', { replace: true });
          }, 3000);

          setIsValidating(false);
          return;
        }

        setHasValidSession(true);
        setIsValidating(false);

      } catch (error) {
        toast({
          title: "Validation Error",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
        setIsValidating(false);
      }
    };

    validateSession();
  }, [toast, navigate]);

  const onSubmit = async (data: { password: string; confirmPassword: string }) => {
    if (!hasValidSession) {
      toast({
        title: "Session Error",
        description: "Invalid session. Please request a new password reset.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data: updateData, error } = await SecurityEnhancedAuth.updatePassword(data.password);

      if (error) {
        // Handle specific error cases
        if (error.message.includes('same as the old password')) {
          toast({
            title: "Password Error",
            description: "New password must be different from your current password.",
            variant: "destructive",
          });
        } else if (error.message.includes('weak password')) {
          toast({
            title: "Weak Password",
            description: "Please choose a stronger password with a mix of letters, numbers, and symbols.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Update Failed",
            description: "Failed to update password. Please try again.",
            variant: "destructive",
          });
        }
        return;
      }

      setIsSuccess(true);

      toast({
        title: "Password Updated!",
        description: "Your password has been successfully updated. You will be signed out for security.",
        duration: 4000,
      });

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Sign out the user and redirect to login after a delay
      setTimeout(async () => {
        try {
          await supabase.auth.signOut();
        } catch (signOutError) {
          // Silent fail - user will be redirected anyway
        }
        navigate('/auth', { replace: true });
      }, 2500);

    } catch (error: any) {
      toast({
        title: "Unexpected Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating session
  if (isValidating) {
    return (
      <div className="space-y-4 text-center">
        <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-center mb-3">
            <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-blue-800 mb-1">Validating Reset Link</h3>
          <p className="text-sm text-blue-600">
            Please wait while we securely verify your password reset link...
          </p>
        </div>
      </div>
    );
  }

  // Show error state if session is invalid
  if (!hasValidSession) {
    return (
      <div className="space-y-4 text-center">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="text-lg font-medium text-red-800 mb-1">Invalid Reset Link</h3>
          <p className="text-sm text-red-600 mb-3">
            This password reset link is invalid or has expired.
          </p>
          <Button 
            onClick={() => navigate('/auth')} 
            variant="outline"
            className="text-red-700 border-red-300 hover:bg-red-50"
          >
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  // Show success state
  if (isSuccess) {
    return (
      <div className="space-y-4 text-center">
        <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-center mb-3">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-green-800 mb-1">Password Updated!</h3>
          <p className="text-sm text-green-600">
            Your password has been successfully updated. You're being signed out for security...
          </p>
        </div>
      </div>
    );
  }

  // Main password reset form
  return (
    <Form {...form}>
      <form 
        onSubmit={form.handleSubmit(onSubmit)} 
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">New Password</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter your new password" 
                  {...field} 
                  type="password" 
                  autoComplete="new-password"
                  className={cn(
                    "transition-all focus-within:ring-2 focus-within:ring-brand-400",
                    isMobile ? "h-12 text-base px-4" : ""
                  )}
                />
              </FormControl>
              <FormMessage className="text-xs" />
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters with uppercase, lowercase, and number
              </p>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-medium">Confirm New Password</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Confirm your new password" 
                  {...field} 
                  type="password" 
                  autoComplete="new-password"
                  className={cn(
                    "transition-all focus-within:ring-2 focus-within:ring-brand-400",
                    isMobile ? "h-12 text-base px-4" : ""
                  )}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        
        <Button 
          type="submit" 
          className="w-full bg-brand-600 hover:bg-brand-700 text-white"
          size={isMobile ? "lg" : "default"}
          disabled={isLoading || !hasValidSession}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating Password...
            </span>
          ) : (
            'Update Password'
          )}
        </Button>
      </form>
    </Form>
  );
}
